import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ZerosixAssetListBlockConfig } from '@front-platform/content-builder';
import { zerosixAssetListBlockResolveConfig } from '@front-platform/content-builder';
import { find } from 'lodash';
import { Component, inject, input } from '@angular/core';
import { map, repeat, shareReplay, switchMap } from 'rxjs/operators';
import type { ZerosixAssetListBlockScenarioContent } from '@front-platform/content-builder';
import { ZEROSIX_ASSET_LIST_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

import { CartService } from '@core/services/cart/cart.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';
import { IZerosixAsset, IZerosixCard, ZerosixAssetType } from '@core/models/zerosix.model';
import {
  ZerosixOfferedProductPickerModalComponent,
  ZerosixOfferedProductPickerModalConfig,
} from '@shared/modals/zerosix/zerosix-offered-product-picker-modal/zerosix-offered-product-picker-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import type { StatedData } from '@shared/utils/rxjs/stated-data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { opacityAnimation } from '@shared/animations/opacity.animation';
export interface ZerosixAssetListBlockRuntimeConfig {}

export type ZerosixAssetListBlockConfigInput = Partial<
  Omit<ZerosixAssetListBlockConfig, keyof ZerosixAssetListBlockRuntimeConfig> &
    ZerosixAssetListBlockRuntimeConfig
>;
export type ZerosixAssetListBlockResolvedConfig = Omit<
  ZerosixAssetListBlockConfig,
  keyof ZerosixAssetListBlockRuntimeConfig
> &
  Partial<ZerosixAssetListBlockRuntimeConfig>;
export interface ZerosixAssetListBlockContract extends ConfigComponentContract<
  ZerosixAssetListBlockConfigInput,
  ZerosixAssetListBlockResolvedConfig
> {}

@Component({
  selector: 'app-zerosix-asset-list-block',
  templateUrl: './zerosix-asset-list-block.component.html',
  animations: [opacityAnimation],
  standalone: false,
})
export class ZerosixAssetListBlockComponent implements ZerosixAssetListBlockContract {
  readonly config = input<ZerosixAssetListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ZerosixAssetListBlockConfig,
    ZerosixAssetListBlockRuntimeConfig
  >(zerosixAssetListBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string {
    return this.resolvedConfig()['heading'] as unknown as string;
  }
  get subheading(): string | undefined {
    return this.resolvedConfig()['subheading'] as unknown as string | undefined;
  }
  get width(): 'centered' | 'full' {
    return this.resolvedConfig()['width'] as unknown as 'centered' | 'full';
  }
  get itemsToShowCardCarousel(): number {
    return this.resolvedConfig()['items_to_show_card_carousel'] as unknown as number;
  }
  get headingColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get assetNameColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['asset_name_color'] as unknown as IContentBuilderFieldColor;
  }
  get assetDetailsColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['asset_details_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }

  /**
   * Used to refresh the user account when it creates his account and validates his registration (at this time, this component, might already loaded)
   */
  protected readonly refreshZerosixAccountInfo$ = new Subject<void>();

  protected readonly selectIsZeroSixPluginEnabled =
    this.marketplaceRefiner.selectIsZeroSixPluginEnabled;

  private readonly hasUserHaveZerosixAccount$ =
    this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$();

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(ZEROSIX_ASSET_LIST_BLOCK_COMPONENT);

  protected readonly userCanLoadZerosix$ = this.cb2PreviewState.isActive()
    ? of(true)
    : combineLatest([this.hasUserHaveZerosixAccount$, this.selectIsZeroSixPluginEnabled]).pipe(
        map(([hasUserHaveZerosixAccount, isZeroSixPluginEnabled]) => {
          return hasUserHaveZerosixAccount && isZeroSixPluginEnabled;
        }),
      );

  protected readonly memberCard$: Observable<StatedData<IZerosixCard | undefined>> =
    this.cb2PreviewState.isActive()
      ? this.previewScenario$.pipe(
          map((scenario): StatedData<IZerosixCard | undefined> => {
            const content = previewScenarioData<ZerosixAssetListBlockScenarioContent>(
              scenario,
              ZEROSIX_ASSET_LIST_BLOCK_COMPONENT,
            );
            if (!content || content.loading || !content.memberCard) {
              return {
                isLoading: true,
                isLoaded: false,
                hasError: false,
                error: undefined,
                result: undefined,
              };
            }
            return {
              isLoading: false,
              isLoaded: true,
              hasError: false,
              error: undefined,
              result: content.memberCard as IZerosixCard,
            };
          }),
        )
      : this.hasUserHaveZerosixAccount$.pipe(
          takeUntilDestroyed(),
          switchMap(hasUserHaveZerosixAccount =>
            hasUserHaveZerosixAccount ? statedStream(this.zerosixService.getCard$()) : EMPTY,
          ),
          repeat({
            delay: () => this.refreshZerosixAccountInfo$,
          }),
          shareReplay(1),
        );
  protected readonly assets$ = this.memberCard$.pipe(
    map(memberCardData => {
      if (memberCardData.isLoading) {
        return [];
      }
      const memberCard = memberCardData.result;
      if (!memberCard) return [];
      const vouchers = memberCard.available_vouchers;
      vouchers.forEach(voucher => {
        voucher.type = ZerosixAssetType.VOUCHER;
        voucher.is_usable = true;
      });

      const rewards = memberCard.rewards;
      rewards.forEach(reward => {
        reward.type = ZerosixAssetType.REWARD;
      });

      return [...vouchers, ...rewards];
    }),
  );
  compartSlides!: IZerosixAsset[][];
  zerosixAssetType = ZerosixAssetType;

  isDesktop$ = this.settingsSelector.selectDeviceIsDesktopScreen;

  constructor(
    private readonly cartService: CartService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly modalsService: ModalsService<ZerosixOfferedProductPickerModalConfig>,
    private readonly zerosixService: ZerosixService,
    protected settingsSelector: SettingsStoreSelector,
    private marketplaceRefiner: MarketplaceStoreRefiner,
  ) {}

  useAsset(asset: IZerosixAsset): void {
    const coupon = this.zerosixService.calculateCouponFromAsset(asset);
    if (!coupon) {
      return;
    }
    this.cartService.addCoupon(coupon);
    if (asset.external_ids.length > 0) {
      this.modalsService.open(ZerosixOfferedProductPickerModalComponent.handle, undefined, {
        asset,
      });
    } else {
      this.updateCart();
    }
  }

  removeAsset(asset: IZerosixAsset): void {
    const coupon = this.zerosixService.calculateCouponFromAsset(asset);
    if (!coupon) {
      return;
    }
    this.cartService.removeCoupon(coupon);
    this.updateCart();
  }

  getAssetTag(voucher: IZerosixAsset): string {
    let description = '';

    if (voucher.value) {
      description += '– ' + voucher.value + ' ';
    }
    if (voucher.unit) {
      description += voucher.unit + ' ';
    }
    return description.trim();
  }

  isAssetInCart(voucher: IZerosixAsset): boolean {
    const coupons = this.cartService.getCouponsInCart();

    return !!find(
      coupons,
      coupon =>
        (coupon.code && coupon.code === voucher.code) ||
        (coupon.key && coupon.key === 'reward_' + voucher.id),
    );
  }

  private updateCart(): void {
    this.orderDispatcher.checkCartIsValid();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ZerosixAssetListBlockComponent, { 'keyAliases': {} });
